import type { NavigateFunction } from "react-router-dom";
import type { UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { userStore } from "../../zustand/globalStore";
import { START_DAILY_REVIEW_ON_ARRIVAL_LOCATION_STATE } from "../../pages/home-page/start-daily-review-on-arrival";
import {
  decidePushNotificationDestination,
  PUSH_NOTIFICATION_OPENED_EVENT,
  readMobilePushNotificationOpenedDeliveryId,
  readMobilePushNotificationOpenedMessage,
  type OpenedPushNotification,
} from "../../utils/analytics/push-notification-opens";
import { identifyUserForAnalytics, trackEvent } from "./helper-methods";

// The mobile app sends a tap as soon as this page has sent `init_app`, which on a cold start is before the
// account has loaded. Reporting then would leave the tap off the learner's profile, and opening the screen then
// could be undone: when the account arrives, AppRouter rewrites the address (it adds the referral parameter),
// which drops a start of today's review carried in the history entry. So both wait until the app knows who is
// signed in (`currentUser` is undefined only until then), and one task more, for what the account's arrival sets
// off. A sign-in that takes longer than this is not waited for.
const MAXIMUM_WAIT_FOR_THE_SIGNED_IN_ACCOUNT_MILLISECONDS = 15_000;

function waitUntilTheSignedInAccountIsKnown(): Promise<UserData | null> {
  const currentUser = userStore.getState().currentUser;
  if (currentUser !== undefined) return Promise.resolve(currentUser);
  return new Promise((resolve) => {
    let unsubscribeFromUserStore = () => {};
    const stopWaiting = () => {
      window.clearTimeout(maximumWaitTimeoutId);
      unsubscribeFromUserStore();
      window.setTimeout(() => resolve(userStore.getState().currentUser ?? null), 0);
    };
    const maximumWaitTimeoutId = window.setTimeout(stopWaiting, MAXIMUM_WAIT_FOR_THE_SIGNED_IN_ACCOUNT_MILLISECONDS);
    unsubscribeFromUserStore = userStore.subscribe((userStoreState) => {
      if (userStoreState.currentUser !== undefined) stopWaiting();
    });
  });
}

/** Reports a tap, on the signed-in learner's profile when there is one. */
export async function reportPushNotificationOpened(
  openedPushNotification: OpenedPushNotification,
  user: UserData | null
): Promise<void> {
  if (user?.date_created) await identifyUserForAnalytics(user).catch(() => {});
  // Explicit null deck: the tap is about the push, not about whichever set happens to be open.
  await trackEvent(PUSH_NOTIFICATION_OPENED_EVENT, openedPushNotification, user, null);
}

/** Opens the screen a push is about, from the page the learner is on. */
export function openPushNotificationDestination(redirectTo: string | null, navigate: NavigateFunction): void {
  const destination = decidePushNotificationDestination(redirectTo, window.location.pathname, window.location.search);
  if (destination.kind === "start_todays_review") {
    // Home's own start request (start-daily-review-on-arrival.ts), not its `?startReview=true` address: home
    // reads that parameter only when the review loads, so it does nothing for a home page that is already
    // open, and a review it starts at once can have its address replaced by AppRouter's rewrite.
    navigate("/", { state: START_DAILY_REVIEW_ON_ARRIVAL_LOCATION_STATE });
  } else if (destination.kind === "open_in_app_path") {
    navigate(destination.inAppPath);
  }
}

/** A tap that reached an open page (the mobile app's bridge event, the desktop service worker's message). */
export async function reportPushNotificationOpenedAndOpenItsScreen(
  openedPushNotification: OpenedPushNotification,
  navigate: NavigateFunction
): Promise<void> {
  const user = await waitUntilTheSignedInAccountIsKnown();
  openPushNotificationDestination(openedPushNotification.redirectTo, navigate);
  await reportPushNotificationOpened(openedPushNotification, user);
}

// The mobile app's taps this page load has handled, by `deliveryId`. The app sends a tap again after the next
// `init_app` while it has not seen the page's confirmation, so a tap can arrive here more than once.
const handledMobilePushNotificationDeliveryIds = new Set<string>();

/**
 * The mobile app's `push_notification_opened` message. Every copy is confirmed to the app at once, with its
 * `deliveryId`, so the app stops sending it; the tap is reported and its screen opened once per `deliveryId`.
 * A message without a `deliveryId` cannot be confirmed and is handled each time it comes.
 */
export function handleMobilePushNotificationOpenedMessage(
  message: unknown,
  confirmReceiptToMobileApp: (deliveryId: string) => void,
  navigate: NavigateFunction
): void {
  const deliveryId = readMobilePushNotificationOpenedDeliveryId(message);
  if (deliveryId !== null) {
    confirmReceiptToMobileApp(deliveryId);
    if (handledMobilePushNotificationDeliveryIds.has(deliveryId)) return;
    handledMobilePushNotificationDeliveryIds.add(deliveryId);
  }
  void reportPushNotificationOpenedAndOpenItsScreen(readMobilePushNotificationOpenedMessage(message), navigate);
}
